import { Alert, Button, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';

const { Text } = Typography;

const colors = {
    primary: '#242166',
    gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
};

// Mêmes valeurs que les fiches agence / courtier / intermédiaire.
const TAUX = [
    { label: 'Exonéré (0 %)', value: 0 },
    { label: '3 %', value: 3 },
    { label: '5 %', value: 5 },
    { label: '20 %', value: 20 },
];

// Une page = une nature : la liste des intermédiaires ne règle que des intermédiaires, etc.
const NATURES = {
    INTERMEDIAIRE: { titre: 'intermédiaires', unite: 'intermédiaire(s)' },
    COURTIER: { titre: 'courtiers', unite: 'courtier(s)' },
    AGENCE: { titre: 'agences', unite: 'agence(s)' },
};

const TYPES = {
    AGENCE: 'Agence',
    SIEGE: 'Siège',
    BUREAU_DIRECT: 'Bureau direct',
    AGENCE_GENERALE: 'Agence générale',
    COURTIER: 'Courtier',
    ETABLISSEMENT_FINANCIER: 'Établissement financier',
    AGENT_GENERAL: 'Agent général',
    APPORTEUR_REGULIER: 'Apporteur régulier',
    APPORTEUR_OCCASIONNEL: 'Apporteur occasionnel',
};

const libelleTaux = (t) => (t === 0 ? 'Exonéré (0 %)' : `${t} %`);
const fcfa = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} FCFA`;

// Le serveur ne renvoie pas le texte de ses erreurs (include-message=never) : on s'appuie sur le statut.
const messageErreur = (err) => {
    switch (err?.response?.status) {
        case 403:
            return "Réservé à l'administrateur du siège (habilitation HPE101), sur les fiches de votre organisme. Un courtier n'est modifiable que si sa convention avec votre compagnie est validée.";
        case 409:
            return "Certaines fiches ont été modifiées entre-temps : la liste a été rechargée, recommencez.";
        case 422:
            return "Demande incomplète : cochez au moins une ligne, choisissez un taux et indiquez un motif.";
        default:
            return "Erreur lors de la modification du taux de retenue fiscale.";
    }
};

/**
 * Modification groupée du taux de retenue fiscale, réservée à l'administrateur du siège, pour UNE
 * nature à la fois (celle de la page qui l'ouvre) : toutes les fiches de cette nature dans son
 * organisme, sélection conservée d'une page à l'autre (et reprise des lignes cochées sur la page),
 * aperçu de l'effet sur les bordereaux non payés, puis enregistrement en une fois avec un motif.
 */
const RetenueFiscaleModal = ({ open, onClose, onApplied, nature, preselection = [] }) => {
    const [fiches, setFiches] = useState([]);
    const [chargement, setChargement] = useState(false);
    const [recherche, setRecherche] = useState('');
    const [tauxActuel, setTauxActuel] = useState(null);
    const [selection, setSelection] = useState([]);
    const [taux, setTaux] = useState(null);
    const [motif, setMotif] = useState('');
    const [apercu, setApercu] = useState(null);
    const [calcul, setCalcul] = useState(false);
    const [enregistrement, setEnregistrement] = useState(false);
    const libelles = NATURES[nature];
    const estPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    // Courtiers : le Pool règle le taux par défaut, une compagnie SON taux (vide = celui du Pool).
    const aideCourtiers = nature !== 'COURTIER' ? null : estPool
        ? "Taux par défaut des courtiers : il s'applique aux compagnies qui n'ont pas fixé leur propre taux."
        : "Ce taux ne s'applique qu'aux bordereaux de votre compagnie. Sans taux propre, votre compagnie suit le taux du Pool.";
    const libelleParDefaut = nature === 'COURTIER' && !estPool ? ' (taux du Pool)' : ' (par défaut)';

    const charger = (aCocher = []) => {
        setChargement(true);
        axiosInstance
            .get('/api/retenue-fiscale/beneficiaires', { params: { nature } })
            .then((res) => {
                const liste = res.data || [];
                setFiches(liste);
                const presents = new Set(liste.map((f) => f.codeId));
                setSelection(aCocher.filter((codeId) => presents.has(codeId)));
            })
            .catch((err) => toast.error(messageErreur(err)))
            .finally(() => setChargement(false));
    };

    useEffect(() => {
        if (!open) return;
        setSelection([]);
        setTaux(null);
        setMotif('');
        setApercu(null);
        setRecherche('');
        setTauxActuel(null);
        charger(preselection);
        // Rechargé à chaque ouverture seulement : la présélection est celle du moment de l'ouverture.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // L'aperçu affiché ne vaut que pour la sélection et le taux qui l'ont produit.
    useEffect(() => {
        setApercu(null);
    }, [selection, taux]);

    const filtrees = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        return fiches.filter((f) =>
            (tauxActuel === null || f.tauxApplique === tauxActuel)
            && (!q || `${f.code || ''} ${f.nom || ''}`.toLowerCase().includes(q)));
    }, [fiches, recherche, tauxActuel]);

    const tauxPresents = useMemo(
        () => [...new Set(fiches.map((f) => f.tauxApplique))].sort((a, b) => a - b), [fiches]);

    const selectionnerAffichees = () => setSelection([...new Set([...selection, ...filtrees.map((f) => f.codeId)])]);

    const demande = () => ({
        agences: nature === 'INTERMEDIAIRE' ? [] : selection,
        intermediaires: nature === 'INTERMEDIAIRE' ? selection : [],
        taux,
        motif: motif.trim(),
    });

    const surErreur = (err) => {
        toast.error(messageErreur(err));
        if (err?.response?.status === 409) {
            charger();
        }
    };

    const voirEffet = () => {
        setCalcul(true);
        axiosInstance
            .post('/api/retenue-fiscale/apercu', demande())
            .then((res) => setApercu(res.data))
            .catch(surErreur)
            .finally(() => setCalcul(false));
    };

    const enregistrer = () => {
        setEnregistrement(true);
        axiosInstance
            .post('/api/retenue-fiscale/appliquer', demande())
            .then((res) => {
                toast.success(res.data?.message || 'Taux de retenue fiscale mis à jour.');
                onApplied?.({ codeIds: selection, taux });
                onClose();
            })
            .catch(surErreur)
            .finally(() => setEnregistrement(false));
    };

    const columns = [
        { title: 'Code', dataIndex: 'code', width: 110 },
        { title: 'Nom', dataIndex: 'nom' },
        {
            title: 'Type',
            dataIndex: 'type',
            width: 180,
            render: (v, f) => (
                <Space size={4}>
                    <span>{TYPES[v] || v || '—'}</span>
                    {f.actif === false && <Tag>inactif</Tag>}
                </Space>
            ),
        },
        {
            title: 'Taux actuel',
            dataIndex: 'tauxApplique',
            width: 160,
            render: (v, f) => (
                <span>
                    {libelleTaux(v)}
                    {f.tauxRetenueFiscale == null && <Text type="secondary">{libelleParDefaut}</Text>}
                </span>
            ),
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={960}
            destroyOnClose
            title={
                <div style={{ padding: '8px 0', borderBottom: `2px solid ${colors.primary}` }}>
                    <Text strong style={{ fontSize: 18, color: colors.primary }}>
                        Taux de retenue fiscale des {libelles?.titre}
                    </Text>
                </div>
            }
            footer={[
                <Button key="annuler" onClick={onClose}>Annuler</Button>,
                <Button key="effet" onClick={voirEffet} loading={calcul} disabled={!selection.length || taux === null}>
                    Voir l'effet
                </Button>,
                <Button
                    key="enregistrer"
                    type="primary"
                    onClick={enregistrer}
                    loading={enregistrement}
                    disabled={!apercu || !motif.trim()}
                    style={{ background: colors.gradient, border: 'none' }}
                >
                    Enregistrer
                </Button>,
            ]}
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Cochez les ${libelles?.titre} à modifier (ou filtrez, puis « Tout cocher »), choisissez le taux, vérifiez l'effet, puis enregistrez.`}
                description={
                    <>
                        {aideCourtiers && <div>{aideCourtiers}</div>}
                        Les bordereaux de commission non payés sont recalculés ; ceux qui étaient validés devront être validés de nouveau. Les bordereaux payés ne changent pas.
                    </>
                }
            />

            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search
                    allowClear
                    placeholder="Code ou nom"
                    style={{ width: 240 }}
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                />
                <Select
                    allowClear
                    placeholder="Taux actuel"
                    style={{ width: 170 }}
                    value={tauxActuel}
                    onChange={(v) => setTauxActuel(v ?? null)}
                    options={tauxPresents.map((t) => ({ label: libelleTaux(t), value: t }))}
                />
                <Button onClick={selectionnerAffichees} disabled={!filtrees.length}>
                    Tout cocher ({filtrees.length})
                </Button>
                <Button onClick={() => setSelection([])} disabled={!selection.length}>
                    Tout décocher
                </Button>
                <Text strong style={{ color: colors.primary }}>{selection.length} {libelles?.unite} coché(s)</Text>
            </Space>

            <Table
                size="small"
                rowKey="codeId"
                loading={chargement}
                columns={columns}
                dataSource={filtrees}
                rowSelection={{ selectedRowKeys: selection, onChange: setSelection, preserveSelectedRowKeys: true }}
                pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 50, 100] }}
            />

            <Space align="start" wrap style={{ width: '100%', marginTop: 8 }}>
                <div>
                    <Text strong>Nouveau taux</Text>
                    <div>
                        <Select
                            placeholder="Choisir"
                            style={{ width: 180 }}
                            value={taux}
                            onChange={setTaux}
                            options={TAUX}
                        />
                    </div>
                </div>
                <div style={{ minWidth: 320, flex: 1 }}>
                    <Text strong>Motif</Text>
                    <Input.TextArea
                        rows={2}
                        maxLength={300}
                        showCount
                        value={motif}
                        onChange={(e) => setMotif(e.target.value)}
                        placeholder="Ex. : liste des taux 2026 transmise par la direction"
                    />
                </div>
            </Space>

            {apercu && (
                <Alert
                    type={apercu.nbBordereauxADevalider ? 'warning' : 'success'}
                    showIcon
                    style={{ marginTop: 16 }}
                    message={`${apercu.nbFiches} ${libelles?.unite} passeront à ${libelleTaux(apercu.taux)}`
                        + (apercu.nbFichesDejaAuTaux ? ` (${apercu.nbFichesDejaAuTaux} y sont déjà).` : '.')}
                    description={apercu.nbBordereaux ? (
                        <>
                            {apercu.nbBordereaux} bordereau(x) de commission non payé(s) seront recalculé(s) :
                            retenue {fcfa(apercu.retenueAvant)} → {fcfa(apercu.retenueApres)}.
                            {apercu.nbBordereauxADevalider > 0 && (
                                <>
                                    <br />
                                    {apercu.nbBordereauxADevalider} étai(en)t validé(s) : à valider de nouveau avant paiement.
                                </>
                            )}
                        </>
                    ) : "Aucun bordereau de commission non payé n'est concerné."}
                />
            )}
        </Modal>
    );
};

export default RetenueFiscaleModal;
